// API client tipado pra `/api/clients` — wrapper sobre `apiClient` (axios).
//
// Backend está em construção em paralelo (Bruno). O tipo `ClientEntity` aqui
// é uma reconstrução defensiva baseada no schema do shared (`createClientSchema`)
// + os campos infraestruturais conhecidos do contrato AC (id, version, audit).
// Quando Bruno publicar um tipo `Client` no `@control-center/shared`, basta
// trocar essa interface por um re-export.
//
// TODO(F1): substituir `ClientEntity` por `Client` de `@control-center/shared` quando Bruno terminar B1.

import type {
  ClientStatus,
  ClientType,
  DocumentType,
  PaginatedResponse,
} from '@control-center/shared';
import type { CreateClientDto, UpdateClientDto } from '@control-center/shared';

import { apiClient } from './api-client';

export type ClientCreatedVia =
  | 'control_center_admin'
  | 'saas_direct'
  | 'migration'
  | 'bootstrap_import';

/** Origens visíveis em badges multi (DR-014). Vem do backend pre-computado. */
export type OriginBadge = 'owl_managed' | 'saas_direct' | 'manual' | 'bootstrap';

/** Estrutura completa do Client retornada pelo GET. */
export interface ClientEntity {
  id: string;
  type: ClientType;
  name: string;
  trade_name: string | null;
  document: string | null;
  document_type: DocumentType;
  inscricao_municipal: string | null;
  inscricao_estadual: string | null;

  email: string | null;
  fiscal_email: string | null;
  financial_email: string | null;
  phone: string | null;
  whatsapp: string | null;
  financial_whatsapp: string | null;
  preferred_channel: 'whatsapp' | 'email' | 'ambos';

  address_street: string | null;
  address_number: string | null;
  address_complement: string | null;
  address_neighborhood: string | null;
  address_city: string | null;
  address_state: string | null;
  address_zip_code: string | null;
  address_country: string;

  responsavel: string | null;
  observations: string | null;
  status: ClientStatus;
  is_iss_exempt: boolean;
  skip_dunning: boolean;
  skip_auto_suspension: boolean;

  created_via: ClientCreatedVia;
  origins_badges: OriginBadge[];

  version: number;
  created_at: string;
  updated_at: string;
  end_date: string | null;
  anonymized: boolean;

  /**
   * Story 6.10 — flag de bounce permanente do email do cliente (5.9).
   * Backend retorna em camelCase (Prisma raw `Client`).
   * Quando preenchido, sinaliza que o email atual está marcado inválido
   * e o cliente não recebe mais emails. Admin pode resetar via
   * POST /api/admin/notifications/clients/:id/reset-email-invalid.
   */
  emailInvalidAt?: string | null;

  /**
   * Story 10.9 (E10 fiscal) — override permanente do CPF/CNPJ pra emissão de NF.
   * Quando preenchido, todas as NFs emitidas usam esse documento ao invés
   * do `document` principal. Útil pra holdings/matrizes/filiais (DR-036).
   * Backend retorna camelCase (Prisma raw); mantemos camelCase aqui pra
   * fiel ao response shape — o legacy snake_case acima é débito conhecido.
   */
  fiscalDocumentOverride?: string | null;
  fiscalDocumentOverrideSetAt?: string | null;
  fiscalDocumentOverrideSetByUserId?: string | null;
}

/** Backend retorna `{ data: ... }` (single resource). Frontend desempacota. */
interface SingleResourceEnvelope<T> {
  data: T;
}

export interface ClientListParams {
  cursor?: string;
  limit?: number;
  q?: string;
  status?: ClientStatus;
  created_via?: ClientCreatedVia;
}

export type ClientListResponse = PaginatedResponse<ClientEntity>;

/** GET /api/clients — lista paginada por cursor. */
export async function fetchClients(
  params: ClientListParams = {},
  signal?: AbortSignal,
): Promise<ClientListResponse> {
  const { data } = await apiClient.get<ClientListResponse>('/api/clients', {
    params,
    signal,
  });
  return data;
}

/** GET /api/clients/:id — detalhe único. Backend wrappeia em `{ data }`. */
export async function fetchClient(id: string, signal?: AbortSignal): Promise<ClientEntity> {
  const { data } = await apiClient.get<SingleResourceEnvelope<ClientEntity>>(`/api/clients/${id}`, {
    signal,
  });
  return data.data;
}

/** POST /api/clients */
export async function createClient(payload: CreateClientDto): Promise<ClientEntity> {
  const { data } = await apiClient.post<SingleResourceEnvelope<ClientEntity>>(
    '/api/clients',
    payload,
  );
  return data.data;
}

/** PATCH /api/clients/:id — manda `If-Match: <version>` para optimistic locking (AC4). */
export async function updateClient(id: string, payload: UpdateClientDto): Promise<ClientEntity> {
  const { version, ...rest } = payload;
  const { data } = await apiClient.patch<SingleResourceEnvelope<ClientEntity>>(
    `/api/clients/${id}`,
    rest,
    { headers: { 'If-Match': String(version) } },
  );
  return data.data;
}

/** PATCH /api/clients/:id/cancel — soft delete via status (AC6). */
export async function cancelClient(id: string, version: number): Promise<ClientEntity> {
  const { data } = await apiClient.patch<SingleResourceEnvelope<ClientEntity>>(
    `/api/clients/${id}/cancel`,
    {},
    { headers: { 'If-Match': String(version) } },
  );
  return data.data;
}

// =============================================================================
// Story 6.10 — Reset email-invalid flag
// =============================================================================

/**
 * POST /api/admin/notifications/clients/:clientId/reset-email-invalid
 * Resposta: { data: { clientId: string; reset: true } }
 */
export async function resetClientEmailInvalid(
  clientId: string,
): Promise<{ clientId: string; reset: true }> {
  const { data } = await apiClient.post<SingleResourceEnvelope<{ clientId: string; reset: true }>>(
    `/api/admin/notifications/clients/${clientId}/reset-email-invalid`,
    {},
  );
  return data.data;
}

// =============================================================================
// Audit logs (Story 2.6) — Zod response parse (Tier 2 obrigatório)
// =============================================================================

import { z } from 'zod';

const auditActionSchema = z.enum(['create', 'update', 'delete', 'state_change', 'custom']);
export type AuditActionType = z.infer<typeof auditActionSchema>;

const auditLogEntrySchema = z.object({
  id: z.string().uuid(),
  userId: z.string().nullable(),
  entityType: z.string(),
  entityId: z.string(),
  action: auditActionSchema,
  oldValue: z.unknown().nullable(),
  newValue: z.unknown().nullable(),
  metadata: z.unknown().nullable(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  createdAt: z.string(),
  user: z.object({ id: z.string(), name: z.string(), email: z.string() }).nullable(),
});
export type AuditLogEntry = z.infer<typeof auditLogEntrySchema>;

const auditLogsResponseSchema = z.object({
  items: z.array(auditLogEntrySchema),
  meta: z.object({
    next_cursor: z.string().nullable(),
    total_estimate: z.number().nullable(),
  }),
});
export type AuditLogsResponse = z.infer<typeof auditLogsResponseSchema>;

export interface AuditLogsQuery {
  cursor?: string;
  limit?: number;
  action?: AuditActionType;
}

/** GET /api/clients/:id/audit-logs */
export async function fetchClientAuditLogs(
  clientId: string,
  query: AuditLogsQuery = {},
): Promise<AuditLogsResponse> {
  const { data } = await apiClient.get(`/api/clients/${clientId}/audit-logs`, {
    params: query,
  });
  return auditLogsResponseSchema.parse(data);
}
